import { HttpClient } from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {Capacitor, CapacitorGlobal} from '@capacitor/core';
import {Directory, Filesystem, FilesystemPlugin, ReadFileResult} from '@capacitor/filesystem';
import {BehaviorSubject} from 'rxjs';
import {CustomCameraMediaItem, CustomCameraMediaType, CustomCameraMimeType, } from './custom-camera';
import {blobToBase64} from '../../shared/camera/camera.service';
import {CaptureService} from '../../shared/capture/capture.service';
import {FILESYSTEM_PLUGIN} from '../../shared/capacitor-plugins/capacitor-plugins.module';
import {CameraPreview, CameraPreviewFlashMode, CameraPreviewOptions, CameraPreviewPictureOptions} from '@capacitor-community/camera-preview';
import {base64ToBlob} from '../../utils/encoding/encoding';
import {v4 as uuidv4} from 'uuid';
import {Media, MediaSaveOptions} from '@capacitor-community/media';

@Injectable({
  providedIn: 'root',
})
export class CustomCameraService {
  // private readonly globalCSSClass = 'custom-camera-transparent-background';
  private previewActive = false;
  private stopInFlight = false;
  private flashEnabled = false;

  uploadInProgress$ = new BehaviorSubject<boolean>(false);
  private readonly platform: CapacitorGlobal;
  private readonly cameraHeaderBase = 30;
  private readonly cameraFooterBase = 150;
  private cameraPreviewOptions: CameraPreviewOptions = {
    position: 'rear',
    parent: 'camera-preview',
    y: this.cameraHeaderBase,
    paddingBottom: this.cameraFooterBase + this.cameraHeaderBase,
    lockAndroidOrientation: true,
    enableHighResolution: true,
    enableZoom: true,
  };

  constructor(
    private readonly sanitizer: DomSanitizer,
    private readonly httpClient: HttpClient,
    private readonly captureService: CaptureService,
    @Inject(FILESYSTEM_PLUGIN)
    private readonly filesystemPlugin: FilesystemPlugin,
  ) {
    this.platform = Capacitor;
  }

  public async mediaItemFromFilePath(filePath: string, type: CustomCameraMediaType, base64: string = null) {
    const isIos = Capacitor.getPlatform() === 'ios';
    const src = (isIos && base64) ? base64 : this.toWebViewUrl(filePath);
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(src);
    const mimeType: CustomCameraMimeType = type === 'image' ? 'image/jpeg' : 'video/mp4';

    return {filePath, base64, src, safeUrl, type, mimeType};
  }

  async uploadToCapture(filePath: string, type: CustomCameraMediaType) {
    const itemToUpload = await this.mediaItemFromFilePath(filePath, type);

    try {
      const itemBlob = await this.httpClient
        .get(itemToUpload.src, { responseType: 'blob' })
        .toPromise();
      const base64 = await blobToBase64(itemBlob);
      const mimeType = itemToUpload.mimeType;
      await this.captureService.capture({ base64, mimeType });
      await this.removeFile(filePath);
    } catch (error) {
      const errMsg = '';
      // await this.errorService.toastError$(errMsg).toPromise();
    }
  }

  async startPreviewCamera() {
    if (this.previewActive || this.stopInFlight) { return; }
    try {
      this.applySafeAreaOffsets();
      await CameraPreview.start(this.cameraPreviewOptions);
      this.logPreviewLayoutDebug();
      this.previewActive = true;
    } catch (error: any) {
      this.previewActive = false;
      const msg = String(error?.errorMessage || error?.message || '').toLowerCase();
      if (!msg.includes('failed to access device camera')) {
        throw error;
      }
    }
    // return PreviewCamera.startPreview()
    //   .then(this.changeGlobalCSSBackgroundToTransparent.bind(this))
    //   .catch((e) => console.log(e));
  }

  private applySafeAreaOffsets() {
    if (!this.platform.isNativePlatform()) { return; }
    const root = document.documentElement;
    const rootStyles = getComputedStyle(root);
    const insetTop = this.parseSafeAreaValue(
      rootStyles.getPropertyValue('--safe-area-inset-top') || root.style.getPropertyValue('--safe-area-inset-top')
    );
    const insetBottom = this.parseSafeAreaValue(
      rootStyles.getPropertyValue('--safe-area-inset-bottom') || root.style.getPropertyValue('--safe-area-inset-bottom')
    );
    const viewportHeight = Math.ceil(window.visualViewport?.height || window.innerHeight || 0);
    const viewportWidth = Math.ceil(window.visualViewport?.width || window.innerWidth || 0);

    const topOverlayHeight = Math.ceil(this.cameraHeaderBase + insetTop);
    const bottomOverlayHeight = Math.ceil(this.cameraFooterBase + insetBottom);
    const finalY = 0;
    const finalHeight = Math.max(0, viewportHeight);
    const platform = Capacitor.getPlatform();
    const isIos = platform === 'ios';

    // if (isIos) {
      // Explicit frame prevents invisible native preview hitbox above the camera area on iOS.
      this.cameraPreviewOptions = {
        ...this.cameraPreviewOptions,
        x: 0,
        y: finalY,
        width: viewportWidth,
        height: finalHeight,
        paddingBottom: 0,
        toBack: true
      };

    console.log('[CustomCamera] layout inputs', {
      platform,
      mode: 'fullscreen',
      safeAreaTop: insetTop,
      safeAreaBottom: insetBottom,
      topOverlayHeight,
      bottomOverlayHeight,
      viewportWidth,
      viewportHeight,
      finalY,
      finalHeight,
      cameraPreviewOptions: this.cameraPreviewOptions,
    });
  }

  private logPreviewLayoutDebug() {
    setTimeout(() => {
      const previewHost = document.getElementById('camera-preview');
      const header = document.querySelector('.camera-header') as HTMLElement | null;
      const footer = document.querySelector('.camera-footer') as HTMLElement | null;
      const previewRect = previewHost?.getBoundingClientRect();
      const headerRect = header?.getBoundingClientRect();
      const footerRect = footer?.getBoundingClientRect();
      console.log('[CustomCamera] post-start DOM layout', {
        previewHostRect: previewRect ? {
          top: Math.round(previewRect.top),
          bottom: Math.round(previewRect.bottom),
          height: Math.round(previewRect.height),
        } : null,
        headerRect: headerRect ? {
          top: Math.round(headerRect.top),
          bottom: Math.round(headerRect.bottom),
          height: Math.round(headerRect.height),
        } : null,
        footerRect: footerRect ? {
          top: Math.round(footerRect.top),
          bottom: Math.round(footerRect.bottom),
          height: Math.round(footerRect.height),
        } : null,
        cameraPreviewOptions: {
          y: this.cameraPreviewOptions.y,
          paddingBottom: this.cameraPreviewOptions.paddingBottom,
        },
      });
    }, 150);
  }

  private parseSafeAreaValue(value: string) {
    if (!value) { return 0; }
    const normalized = value.trim();
    const matches = normalized.match(/(-?[\d.]+)px/g);
    if (matches?.length) {
      // Computed style may return expressions like `max(47px, 0px)`.
      // Use the biggest parsed inset instead of the last token.
      const parsedMatches = matches
        .map(match => Number.parseFloat(match.replace('px', '')))
        .filter(parsed => Number.isFinite(parsed));
      return parsedMatches.length ? Math.max(...parsedMatches) : 0;
    }
    const parsed = Number.parseFloat(normalized.replace('px', '').trim());
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private toWebViewUrl(filePath: string): string {
    if (!filePath) { return filePath; }
    if (filePath.startsWith('data:')) { return filePath; }
    const converted = Capacitor.convertFileSrc(filePath);
    if (converted.startsWith('capacitor://localhost/')) {
      return converted.replace('capacitor://localhost/', 'http://localhost/');
    }
    return converted;
  }

  async stopPreviewCamera() {
    if (!this.previewActive || this.stopInFlight) { return; }
    this.stopInFlight = true;
    try {
      await CameraPreview.stop();
    } catch (error: any) {
      const msg = String(error?.errorMessage || error?.message || '').toLowerCase();
      if (!msg.includes('camera already stopped')) {
        throw error;
      }
    } finally {
      this.previewActive = false;
      this.stopInFlight = false;
    }
  }

  // eslint-disable-next-line class-methods-use-this
  async flipCamera() {
    if (!this.isNativePlatform) { return; }
    await CameraPreview.flip();
  }

  // eslint-disable-next-line class-methods-use-this
  async takePhoto() {
    const cameraPreviewPictureOptions: CameraPreviewPictureOptions = {
      quality: 100,
    };

    // const result = await PreviewCamera.takePhoto();
    const result = await CameraPreview.capture(cameraPreviewPictureOptions);
    const rawBase64 = this.normalizeBase64(result?.value);
    if (!rawBase64) {
      throw new Error('Camera capture returned empty data');
    }
    const base64PictureData = `data:image/jpeg;base64,${rawBase64}`;
    const blob = await base64ToBlob(rawBase64, 'image/jpeg');
    const id = uuidv4();
    let filePath = null;
    console.log(this.platform.isNativePlatform(), 'test');
    if (this.platform.isNativePlatform()) {
      const albumIdentifier = await this.ensureDemoAlbum();
      const opts: MediaSaveOptions = { path: base64PictureData, fileName: `${id}.jpeg` };
      if (albumIdentifier) {
        opts.albumIdentifier = albumIdentifier;
      }
      const res = await Media.savePhoto(opts);
      filePath = res?.filePath || base64PictureData;
    } else {
      // @ts-ignore
      filePath = URL.createObjectURL(blob);
    }

    const base64ForItem = Capacitor.getPlatform() === 'ios' ? base64PictureData : null;
    return this.mediaItemFromFilePath(filePath, 'image', base64ForItem);
  }

  public startRecord(): void {
    CameraPreview.startRecordVideo(this.cameraPreviewOptions).catch((reason) => console.log(reason));
  }

  async stopRecord(): Promise<CustomCameraMediaItem> {
    const resultRecordVideo = await CameraPreview.stopRecordVideo();
    const id = uuidv4();
    let filePath = resultRecordVideo.videoFilePath;
    try {
      const albumIdentifier = await this.ensureDemoAlbum();
      const opts: MediaSaveOptions = { path: resultRecordVideo.videoFilePath, fileName: id };
      if (albumIdentifier) {
        opts.albumIdentifier = albumIdentifier;
      }
      const res = await Media.saveVideo(opts);
      filePath = res?.filePath || resultRecordVideo.videoFilePath;
    } catch (error) {
      console.warn('[CustomCamera] saveVideo failed, using original path', error);
    }
    let base64VideoData: string | null = null;
    if (Capacitor.getPlatform() === 'ios') {
      try {
        const read = await this.readFile(filePath);
        if (read?.data) {
          base64VideoData = `data:video/mp4;base64,${read.data}`;
        }
      } catch (e) {
        console.log('[CustomCamera] video base64 fallback unavailable', e);
      }
    }
    return this.mediaItemFromFilePath(filePath, 'video', base64VideoData);
  }

  async ensureDemoAlbum(): Promise<string | undefined> {
    const { albums } = await Media.getAlbums();
    let demoAlbum;
    let albumsPath: string | undefined;
    if (Capacitor.getPlatform() === 'android') {
      albumsPath = (await Media.getAlbumsPath()).path;
      demoAlbum = albums.find(a => a.name === 'WatchThisGarage' && a.identifier?.startsWith(albumsPath));
    } else {
      demoAlbum = albums.find(a => a.name === 'WatchThisGarage');
    }

    if (!demoAlbum) {
      demoAlbum = await Media.createAlbum({ name: 'WatchThisGarage' });
    }

    if (demoAlbum?.identifier) {
      return demoAlbum.identifier;
    }

    if (Capacitor.getPlatform() === 'android' && albumsPath) {
      return `${albumsPath}/WatchThisGarage`;
    }

    return undefined;
  }

  async readFile(filePath: string): Promise<ReadFileResult> {
    // Here's an example of reading a file with a full file path. Use this to
    // read binary data (base64 encoded) from plugins that return File URIs, such as
    // the Camera.
    const contents = await Filesystem.readFile({
      path: filePath // 'file:///var/mobile/Containers/Data/Application/22A433FD-D82D-4989-8BE6-9FC49DEA20BB/Documents/text.txt',
    });

    console.log('data:', contents);
    return contents;
  }

  async removeFile(filePath: string | undefined) {
    if (!filePath) { return; }
    await this.filesystemPlugin.deleteFile({ path: filePath });
  }

  async isTorchOn() {
    return { result: this.flashEnabled };
  }

  async enableTorch(enable: boolean): Promise<void> {
    if (!this.isNativePlatform) { return; }
    const supported = await this.getSupportedFlashModes();
    if (!supported.length) { return; }
    const enableMode: CameraPreviewFlashMode | null = supported.includes('torch')
      ? 'torch'
      : supported.includes('on') ? 'on' : null;
    const disableMode: CameraPreviewFlashMode | null = supported.includes('off')
      ? 'off'
      : null;
    if (enable && enableMode) {
      await CameraPreview.setFlashMode({ flashMode: enableMode });
      this.flashEnabled = true;
    }
    if (!enable && disableMode) {
      await CameraPreview.setFlashMode({ flashMode: disableMode });
      this.flashEnabled = false;
    }
  }

  async focus(x: number, y: number) {
    if (this.isNativePlatform) {
      // return await PreviewCamera.focus({ x, y });
    }
    return Promise.resolve();
  }

  async minZoomFactor(): Promise<number> {
    const defaultMinAvailableZoom = 0;
    return defaultMinAvailableZoom;
  }

  async maxZoomFactor(): Promise<number> {
    const defaultMaxAvailableZoom = 0;
    return Promise.resolve(defaultMaxAvailableZoom);
  }

  async zoom(zoomFactor: any) {
    return Promise.resolve();
  }

  async isTorchAvailable(): Promise<boolean> {
    if (!this.isNativePlatform) { return false; }
    const modes = await this.getSupportedFlashModes();
    return modes.includes('torch') || modes.includes('on');
  }

  private async getSupportedFlashModes(): Promise<CameraPreviewFlashMode[]> {
    if (!this.isNativePlatform) { return []; }
    try {
      const res = await CameraPreview.getSupportedFlashModes();
      return (res?.result || []) as CameraPreviewFlashMode[];
    } catch (error) {
      return [];
    }
  }

  async setCameraQuality(quality: 'low' | 'hq'): Promise<void> {
    if (!this.isNativePlatform) { return; }

    // await PreviewCamera.setQuality({ quality }).catch((e) => console.log(e));
  }

  private get isNativePlatform() {
    return this.platform.isNativePlatform();
  }

  private normalizeBase64(value?: string): string | null {
    if (!value) { return null; }
    if (value.startsWith('data:')) {
      const marker = 'base64,';
      const idx = value.indexOf(marker);
      if (idx === -1) { return null; }
      return value.slice(idx + marker.length);
    }
    return value;
  }
}
